import type { Dispatch, FC, SetStateAction } from 'react';
import React, { useEffect, useMemo, useRef } from 'react';

import { clsx } from 'clsx';

import { OVERLAY_CLOSE_ELEMENT_PROPS } from '../../OverlayTreeItem';
import {
    cnDrawerContent,
    cnDrawerCurtain,
    cnDrawerCurtainContent,
    cnDrawerDragObserver,
    cnDrawerHandle,
    cnDrawerOverlay,
    cnDrawerTitle,
} from '../Drawer.cn';
import type { IDrawerProps } from '../Drawer.types';
import { noop, useDrag } from '../Drawer.utils';

/**
 * Промежуточное состояние компонента между событиями touchstart и touchend
 */
interface IDragStateData {
    isTargetUnderContent: boolean;
    initialScrollPosition: number;
    isScrolled: boolean;
}

/**
 * Пропы наследуются из основного компонента
 */
interface IDrawerContentProps extends IDrawerProps {
    springValue: number;
    setProgress: Dispatch<SetStateAction<number>>;
    setSpringDisabled: Dispatch<SetStateAction<boolean>>;
    closing: boolean;
    setClosing: Dispatch<SetStateAction<boolean>>;
}

interface IGetSpringTransformParams {
    axis: 'x' | 'y';
    springValue: number;
    inverted: 1 | -1;
}

function getSpringTransform({
    axis,
    springValue,
    inverted,
}: IGetSpringTransformParams): string | null {
    if (springValue === 1) {
        return null;
    }

    return axis === 'x'
        ? `translate3d(${(1 - springValue) * 100 * inverted}%,0,0)`
        : `translate3d(0,${(1 - springValue) * 100 * inverted}%,0)`;
}

function getFirstInteractiveElementInScope(scopeRef: HTMLDivElement) {
    const interactiveElements = Array.from(
        scopeRef.querySelectorAll('a[href], button, input, select, textarea, [tabindex="0"]'),
    );

    for (let element of interactiveElements) {
        const computedStyle = window.getComputedStyle(element);
        const isHidden =
            element.getAttribute('aria-hidden') === 'true' || computedStyle?.display === 'none';
        const hasAriaHiddenParent = element.closest('[aria-hidden="true"]') !== null;
        const hasDisplayHiddenParent = element.closest('[style*="display: none"]') !== null;
        const hasHiddenParent = hasAriaHiddenParent || hasDisplayHiddenParent;
        const hasText = element?.textContent?.trim() !== '';

        if (!isHidden && !hasHiddenParent && (hasText || element?.tagName === 'BUTTON')) {
            return element as HTMLElement;
        }
    }

    return;
}

type InitialScrollingParams = {
    content: HTMLElement;
    target: HTMLElement;
    isTargetInContent: boolean;
    direction: NonNullable<IDrawerContentProps['direction']>;
};

/**
 * Определить размер скроллинга по указанному направлению
 * @param param параметры
 * @returns значение скроллинга
 */
function getScrolling({
    content,
    target,
    isTargetInContent,
    direction,
}: InitialScrollingParams): number {
    if (!isTargetInContent) {
        return 0;
    }

    for (let node: HTMLElement | null = target; node; node = node.parentElement) {
        let scrolling = 0;

        switch (direction) {
            case 'bottom':
                scrolling = node.scrollTop;
                break;
            case 'right':
                scrolling = node.scrollLeft;
                break;
            case 'top':
                scrolling = node.scrollHeight - node.clientHeight - node.scrollTop;
                break;
            case 'left':
                scrolling = node.scrollWidth - node.clientWidth - node.scrollLeft;
                break;
            default:
                direction satisfies never;
        }

        if (scrolling || node === content) {
            return scrolling ?? 0;
        }
    }
    return 0;
}

/**
 * Компонент содержимого шторки.
 *
 * При использовании Popup мы не знаем в какой конкретно
 * момент содержимое монтируется/размонтируется, если
 * прокидывать его напрямую без обёрток.
 */
export const DrawerContent: FC<IDrawerContentProps> = ({
    curtainClassName,
    dragDisabled,
    visible,
    onClose = noop,
    onCloseEnd = noop,
    onClick = noop,
    springValue,
    direction = 'bottom',
    maxSize,
    titleComponent,
    children,
    setSpringDisabled,
    setProgress,
    autoFocus = false,
    slotTop,
    slotBottom,
    dialogAriaLabel,
    closing,
    setClosing,
}) => {
    const contentRef = useRef<HTMLDivElement>(null);

    const axis = direction === 'bottom' || direction === 'top' ? 'y' : 'x';
    const inverted = direction === 'left' || direction === 'top' ? -1 : 1;

    const springOpacity = Math.max(springValue, 0);
    const springTransform = getSpringTransform({ axis, springValue, inverted });

    const curtainRef = useRef<HTMLDivElement>(null);
    const firstAutoFocus = useRef(autoFocus).current;

    useEffect(() => {
        if (visible && firstAutoFocus && springValue === 1) {
            const element =
                curtainRef?.current && getFirstInteractiveElementInScope(curtainRef.current);

            if (element) {
                element.focus();

                /**
                 * Если drawer открывается дольше 600 мс, то при открытии шторки
                 * элемент на котором сейчас находится фокус имеет обводку (outline).
                 */
                document.body.classList.remove('utilityfocus');
                document.body.classList.add('pointerfocus');
            } else {
                curtainRef.current?.focus();
            }
        }
    }, [visible, springValue, firstAutoFocus]);

    const curtainStyle = useMemo(
        () => ({
            ...(springTransform ? { transform: springTransform } : {}),
            ...(maxSize && { [axis === 'x' ? 'maxWidth' : 'maxHeight']: maxSize }),
        }),
        [springTransform, maxSize, axis],
    );

    useEffect(() => {
        if (!visible) {
            setClosing(true);
        }
    }, [visible, setClosing]);

    useEffect(() => {
        if (closing && springValue === 0) {
            onCloseEnd();
            setClosing(false);
        }
    }, [closing, springValue, onCloseEnd, setClosing]);

    /**
     * Обработчик drag событий с корневого DOM элемента шторки
     */
    const { dragProps } = useDrag<IDragStateData>((dragState) => {
        if (!visible || !contentRef.current) return;

        const {
            velocity: { x: vx, y: vy },
            movement: { x: mx, y: my },
            first,
            last,
            data,
            event,
        } = dragState;

        const drawerSize =
            axis === 'x' ? contentRef.current.clientWidth : contentRef.current.clientHeight;
        const movement = inverted * (axis === 'x' ? mx : my);
        const velocity = inverted * (axis === 'x' ? vx : vy);

        if (
            (axis === 'x' && Math.abs(mx) < Math.abs(my)) ||
            (axis === 'y' && Math.abs(my) < Math.abs(mx))
        ) {
            return;
        }

        if (first) {
            data.isTargetUnderContent = contentRef.current.contains(event.target as HTMLElement);
            data.initialScrollPosition = getScrolling({
                content: contentRef.current,
                target: event.target as HTMLElement,
                direction,
                isTargetInContent: data.isTargetUnderContent,
            });
            data.isScrolled = data.initialScrollPosition !== 0;
        } else {
            data.isScrolled =
                data.isScrolled ||
                getScrolling({
                    content: contentRef.current,
                    target: event.target as HTMLElement,
                    direction,
                    isTargetInContent: data.isTargetUnderContent,
                }) !== 0;
        }

        // ничего не делаем когда жест происходит одновременно с проскроллом
        // или если шторка в статичном состоянии
        // @see SERP-107544
        if (dragDisabled || (data.isTargetUnderContent && data.isScrolled)) {
            return;
        }

        // жест завершен, возвращаем шторку в открытое положение, если
        // скорость была недостаточной и, закрываем если наоборот
        if (last) {
            setSpringDisabled(false);

            if (Math.abs(velocity) >= 0.1) {
                return velocity > 0 ? onClose() : setProgress(1);
            } else if (movement / drawerSize >= 0.3) {
                return onClose();
            }

            return setProgress(1);
        }

        if (movement > 0) {
            setSpringDisabled(true);

            const progress = Math.max(0, 1 - movement / drawerSize);

            if (progress === 0) {
                return onClose();
            }

            return setProgress(progress);
        }
    });

    return (
        <div className={cnDrawerDragObserver} {...dragProps}>
            <div
                className={cnDrawerOverlay}
                style={{ opacity: springOpacity }}
                {...OVERLAY_CLOSE_ELEMENT_PROPS}
            />
            <div
                className={clsx(cnDrawerCurtain, curtainClassName)}
                style={curtainStyle}
                ref={curtainRef}
                tabIndex={-1}
                role="dialog"
                aria-label={dialogAriaLabel}
                aria-modal={springValue === 1}
                onClick={onClick}
            >
                <div className={cnDrawerHandle} style={{ opacity: springOpacity }} />
                <div className={cnDrawerCurtainContent}>
                    {titleComponent && <div className={cnDrawerTitle}>{titleComponent}</div>}
                    <div className={cnDrawerContent} ref={contentRef}>
                        {slotTop && React.cloneElement(slotTop, { ...slotTop.props, place: 'top' })}
                        {children}
                        {slotBottom &&
                            React.cloneElement(slotBottom, {
                                ...slotBottom.props,
                                place: 'bottom',
                            })}
                    </div>
                </div>
            </div>
        </div>
    );
};
